/* eslint-disable @typescript-eslint/no-explicit-any */
import { ServiceBus } from 'elc-service-bus';
import { LoadableComponent } from '@loadable/component';

export const SERVICE_VIEW_CLASSNAME = 'elc-service-view-renderer';
export const SERVICE_VIEW_RENDERED_ATTRIBUTE = 'data-rendered-view';
export const MOBILE_VERSION_SELECTOR = 'js-mobile-version';
export const DESKTOP_VERSION_SELECTOR = 'js-desktop-version';

export interface ISingleGitInfo {
    branchName: string;
    commit: string;
}

export interface IGitInfo {
    [key: string]: ISingleGitInfo;
}

export type ArrayMinOne<T> = {
    0: T;
} & T[];

export type RegisteredComponent = () => Promise<{
    ['default']: React.ComponentType<any>;
}>;

export interface IComponent {
    componentName: string;
    component: LoadableComponent<any>;
    viewController?(): IComponentViewController;
}

export interface IComponentRegistry {
    [key: string]: IComponent;
}
export interface ICachedComponentsRegistry {
    [key: string]: React.ComponentType<any>;
}

interface IObjectOfStrings {
    [key: string]: string;
}

interface IServiceBusTopics {
    commands: IObjectOfStrings;
    events: IObjectOfStrings;
    queries: IObjectOfStrings;
}

export interface IViewControllerData {
    [key: string]: any;
}

export type ViewControllerLoadedMethod = (...args: any[]) => Promise<any>;

export interface IComponentViewController {
    id?: string;
    loaded?: ViewControllerLoadedMethod;
    data?: IViewControllerData;
    dataState?: IViewControllerData;
    configMergeStrategy?: Function;
    initialize?(...args: any[]): void;
    handleState?(...args: any[]): void;
    setSSRState(data: IViewControllerData): void;
    setId(id: string): void;
    isLoading: boolean;
}

export interface IServiceRegistryComponent {
    isLazy?: boolean;
    views?: string[];
    events?: string[];
    queries?: string[];
    name: string;
    version: string;
    isStarted: boolean;
    serviceBusController?: any;
    componentsRegistry: IComponentRegistry;
    cachedComponentsRegistry: ICachedComponentsRegistry;
    serviceInitializer: any;
    start(appConfig: any): Promise<void>;
    register?(): void;
    getView(componentName: string): IComponent | undefined;
    setViewInCache(componentName: string, component: React.ComponentType<any>): void;
    getViewFromCache(componentName: string): React.ComponentType<any>;
    registerView(
        componentName: string,
        component: RegisteredComponent,
        viewController?: () => object
    ): void;
}

interface IServiceRegistry {
    [key: string]: IServiceRegistryComponent;
}
export interface IPayload {
    url: string;
    body?: {};
    options?: {};
    headers?: {};
}

export interface IGetPayload {
    url: string;
    options?: {};
    headers?: {};
    params?: {};
}
export interface IRequestOptions {
    debounceInterval?: number;
}
export type HeaderType = 'Business-Unit' | 'Authorization' | 'AuthorizationToken' | 'ClientId';
export type HeaderObject = { [key in HeaderType]?: string };
export type IHeaderSetter = () => Promise<HeaderObject>;
export type IRequestTransformerConfig = { url: string } & RequestInit;
export type IRequestTransformer = (config: IRequestTransformerConfig) => Promise<void>;
export interface IHttpTransport {
    getStaticGlobalHeaders(): HeaderObject;
    setBaseUrl(baseUrl: string): void;
    addGlobalHeaders(headers: HeaderObject): void;
    removeGlobalHeader(headerName: string): void;
    addGlobalHeaderSetter(setter: IHeaderSetter): void;
    removeGlobalHeaderSetter(setter: IHeaderSetter): void;
    addRequestTransformer(transformer: IRequestTransformer): void;
    removeRequestTransformer(transformer: IRequestTransformer): void;
    delete(payload: string | IPayload): Promise<any>;
    post(payload: IPayload): Promise<any>;
    patch(payload: IPayload): Promise<any>;
    put(payload: IPayload): Promise<any>;
    get(payload: string | IGetPayload, IRequestOptions?: IRequestOptions): Promise<any>;
    readonly isAuthFailureHandlingEnabled: boolean;
    setAuthFailureHandlingEnabled(enabled: boolean): void;
}

export interface IUtagData {
    [key: string]: string | string[] | undefined | boolean | number | null | number[];
}
export interface IUtagCfgOvrd {
    noview: boolean;
    dom_complete?: boolean;
    lowerqp?: boolean;
    noload?: boolean | number;
    nocookie?: boolean;
    readywait?: boolean | number;
    waittimer?: number;
}
export interface IUtag {
    link(data: IUtagData, cb?: Function): void;
    view(data: IUtagData, cb?: Function): void;
}

interface IDrupalSite {
    translations?: any;
    trackingDataLayer?: any;
    client?: {
        subdomain?: string;
        isMobile?: number;
    };
}

interface IComponentRenderer {
    init(initialMarkup?: string): void;
}

type IObjectLike = { [key: string]: any } | null;

declare global {
    // Augment Node.js `global`
    namespace NodeJS {
        interface Global {
            ServiceBusTopics: IServiceBusTopics;
            ServiceRegistry: IServiceRegistry;
            GlobalServiceBus: ServiceBus<any, any, any, any, any>;
            HttpTransport: IHttpTransport;
            __translationsCache__: { [key: string]: string };
            __configsCache__: { [key: string]: any };
            getPreloadedConfigs(fields?: string[]): IObjectLike;
            addConfigs(configs: object): void;
            getPreloadedTranslations(fields?: string[]): IObjectLike;
            addTranslations(translations: object): void;
            replaceSrc(originalSrc: string): string;
            utag_data: IUtagData;
            utag_cfg_ovrd: IUtagCfgOvrd;
            utag: IUtag;
            site?: IDrupalSite;
            location: Location;
            ComponentRenderer: IComponentRenderer;
            registerTranslationFieldsToPreload: Function;
            registerConfigFieldsToPreload: Function;
            versions: IObjectOfStrings;
            getServiceVersions(): void;
            __ssr: boolean;
            onerror(
                message: string,
                url: string,
                line: number,
                column: number,
                error: Error
            ): boolean;
            env: string;
            buid: string;
            gitInfo: IGitInfo;
        }
    }

    // Augment Browser `window`
    interface Window {
        ServiceBusTopics: IServiceBusTopics;
        __translationsCache__: { [key: string]: string };
        __configsCache__: { [key: string]: any };
        getPreloadedConfigs(fields?: string[]): IObjectLike;
        addConfigs(configs: object): void;
        getPreloadedTranslations(fields?: string[]): IObjectLike;
        addTranslations(translations: object): void;
        ServiceRegistry: IServiceRegistry;
        GlobalServiceBus: ServiceBus<any, any, any, any, any>;
        HttpTransport: IHttpTransport;
        replaceSrc(originalSrc: string): string;
        utag_data: any;
        utag_cfg_ovrd: IUtagCfgOvrd;
        utag: IUtag;
        location: Location;
        site?: IDrupalSite;
        addEventListener(a: string, b: Function): void;
        ComponentRenderer: IComponentRenderer;
        registerTranslationFieldsToPreload: Function;
        registerConfigFieldsToPreload: Function;
        versions: IObjectOfStrings;
        getServiceVersions(): void;
        __ssr: boolean;
        onerror(message: string, url: string, line: number, column: number, error: Error): boolean;
        env: string;
        buid: string;
        gitInfo: IGitInfo;
    }

    // Augment Web Worker `self`
    interface WorkerGlobalScope {
        ServiceBusTopics: IServiceBusTopics;
        ServiceRegistry: IServiceRegistry;
        GlobalServiceBus: ServiceBus<any, any, any, any, any>;
        HttpTransport: IHttpTransport;
        __translationsCache__: { [key: string]: string };
        __configsCache__: { [key: string]: any };
        getPreloadedConfigs(fields?: string[]): IObjectLike;
        addConfigs(configs: object): void;
        getPreloadedTranslations(fields?: string[]): IObjectLike;
        addTranslations(translations: object): void;
        replaceSrc(originalSrc: string): string;
        utag_data: IUtagData;
        utag_cfg_ovrd: IUtagCfgOvrd;
        utag: IUtag;
        location: Location;
        addEventListener(a: string, b: Function): void;
        ComponentRenderer: IComponentRenderer;
        registerTranslationFieldsToPreload: Function;
        registerConfigFieldsToPreload: Function;
    }
}

export const isWebRendered = typeof self === 'object' && self.self === self;

export const root: NodeJS.Global | Window =
    (isWebRendered && self) ||
    (typeof global === 'object' && global.global === global && global) ||
    window;

export const registerGitInfo = (serviceName: string, branchName: string, commit: string) => {
    root.gitInfo = root.gitInfo || {};
    root.gitInfo[serviceName] = {
        branchName,
        commit
    };
};
